/**
 * nextScreenImageCandidates — какие картинки имеет смысл подгрузить заранее,
 * пока покупатель стоит на текущем экране (WEB-1995).
 *
 * Чистый модуль: ни DOM, ни сети, ни React. Решает ровно один вопрос — КАКИЕ
 * адреса и в каком порядке. КОГДА и КАК их грузить решает хук
 * `usePrefetchNextScreenImages`.
 *
 * Почему «кандидаты», а не «следующий экран»: следующий экран зависит от
 * ответа. У варианта может быть свой `nextScreenId` (QuizScreenWizard,
 * ветка clickedOption), иначе шаг идёт по порядку. Значит, достижимых одним
 * действием экранов несколько, и подгружать надо их все — с лимитами, чтобы
 * ветвистый экран не выкачал половину воронки.
 *
 * Правила видимости учитываются в ОБЕ стороны:
 *   - невидимый блок кандидата не показывает свою картинку → она не кандидат;
 *   - невидимый блок ТЕКУЩЕГО экрана не диктует ветвь своими вариантами
 *     (зеркало WEB-1456: скрытый блок не участвует в переходе).
 */
import type { EditorBlock, EditorScreen } from '@/store/editorSlice';
import type { EvalContext, Logic } from '@/logic-engine';
import { resolveVisibility } from '@/player/visibility';
import { blockHonorsOptionNav } from '@/logic-engine/funnel-graph';
import { resolveEditorBlockOptions } from '@/components/editor/resolveEditorBlockOptions';
import { isImageUrlKeyOf } from '@/utils/imageUrlFields';

/** Сколько картинок максимум подгружаем заранее с одного экрана. */
export const PREFETCH_MAX_IMAGES = 6;

/**
 * Сколько байт максимум тратим на предзагрузку с одного экрана. Считается
 * ТОЛЬКО по известным размерам: у адреса картинки размера обычно нет, и
 * неизвестный размер бюджет не тратит (иначе лимит по объёму молча превратился
 * бы в запрет предзагрузки вовсе).
 */
export const PREFETCH_MAX_BYTES = 1_500_000;

/**
 * Ключи блока, в которых лежит адрес картинки. Не перечень типов блоков и не
 * ручной список полей по именам блоков: поля ищутся ОБХОДОМ объекта блока по
 * форме имени — новое поле с картинкой (у нового блока или у нового элемента
 * списка) попадает под предзагрузку само, без правки этого файла. Ручной
 * список здесь молчал бы про новое поле по построению (шрам WEB-1687).
 *
 * `imagePrompt` (текст запроса к генератору) намеренно не подходит под форму:
 * это не адрес.
 *
 * Свежий взгляд сведения стильпаков (WEB-3718): форма имени не узнавала
 * значок перед текстом (`leadingIconUrl`) и картинку у заголовка
 * (`sideImageUrl`), а значок-картинку варианта `{kind:'image', url}` не могла
 * узнать вовсе. Поэтому рядом с формой — зеркало серверного реестра адресов
 * картинок (`utils/imageUrlFields.ts`, `isImageUrlKeyOf`): имя из реестра или
 * `url` узла-картинки. Форма осталась второй сетью и ничего не снимает.
 */
const IMAGE_KEY_RE = /^(image|icon|photo|avatar|logo|picture|thumbnail)Url$/;

/** Глубина обхода блока: блок → массив элементов → поля элемента. */
const MAX_WALK_DEPTH = 4;

export interface ScreenImageCandidate {
  /** Экран, куда можно попасть одним действием. */
  screenId: string;
  /**
   * Варианты ответа текущего экрана, ведущие на этот экран. Пусто у ступени
   * «следующий по порядку». Нужны подсказке по намерению: нажатие по варианту
   * поднимает приоритет именно его ветви.
   */
  optionIds: string[];
  /** Все картинки кандидата в порядке показа (фон экрана первым). */
  urls: string[];
  /** Ступень по умолчанию: следующий по порядку либо первый экран пейволла. */
  isDefault: boolean;
}

export interface NextScreenImagePlan {
  candidates: ScreenImageCandidate[];
  /**
   * Плоский список адресов к предзагрузке: сначала экран по умолчанию, потом
   * ветви; без повторов; обрезан лимитами.
   */
  urls: string[];
}

export interface NextScreenImageCandidatesInput {
  screens: readonly EditorScreen[];
  /** Индекс экрана, который покупатель видит сейчас. */
  currentIndex: number;
  logic: Logic;
  /** Снимок состояния для правил видимости — тот же, что кормит hiddenSet. */
  context: EvalContext;
  /**
   * Первый экран пейволла воронки. С ПОСЛЕДНЕГО экрана квиза покупатель
   * уходит туда, и это его ступень по умолчанию. Плеер квиза знает о пейволле
   * только slug (данные подгружаются лениво), поэтому поле необязательное:
   * нет данных — нет кандидата, а не выдуманные адреса.
   */
  paywallFirstScreen?: EditorScreen | null;
  /** Известный размер картинки в байтах, если он откуда-то известен. */
  sizeOfImage?: (url: string) => number | undefined;
  maxImages?: number;
  maxBytes?: number;
  /** Сколько картинок брать с ОДНОГО кандидата (на 3g — одну). */
  maxPerCandidate?: number;
}

/**
 * Адрес, который браузеру есть смысл забрать заранее. `data:`/`blob:` уже
 * лежат в памяти страницы — качать их нечего, а мимо проверки они утащили бы
 * лимит.
 */
function isPrefetchableUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const url = value.trim();
  if (!url) return false;
  return (
    url.startsWith('https://') ||
    url.startsWith('http://') ||
    url.startsWith('/')
  );
}

/** Собрать адреса картинок из произвольного узла блока (обход по форме имени). */
function collectImageUrls(node: unknown, depth: number, out: string[]): void {
  if (depth > MAX_WALK_DEPTH || node === null || typeof node !== 'object')
    return;
  if (Array.isArray(node)) {
    for (const item of node) collectImageUrls(item, depth + 1, out);
    return;
  }
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (
      (key === 'src' ||
        IMAGE_KEY_RE.test(key) ||
        isImageUrlKeyOf(node as Record<string, unknown>, key)) &&
      isPrefetchableUrl(value)
    ) {
      out.push(value.trim());
      continue;
    }
    if (value !== null && typeof value === 'object') {
      collectImageUrls(value, depth + 1, out);
    }
  }
}

/**
 * Картинки одного экрана в порядке показа.
 *
 * Фон экрана — только в режиме `full-screen`: плеер воронки рисует именно его,
 * режим `card` на публичной странице не проявляется (шрам quiz-runtime
 * 02.09.2026, WEB-1873/WEB-927). Подгружать невидимое — трата полосы.
 */
function screenImageUrls(
  screen: EditorScreen,
  logic: Logic,
  context: EvalContext,
): string[] {
  const urls: string[] = [];
  if (
    screen.background?.mode === 'full-screen' &&
    isPrefetchableUrl(screen.background.imageUrl)
  ) {
    urls.push(screen.background.imageUrl.trim());
  }
  const hidden = resolveVisibility(screen.blocks ?? [], logic, context);
  for (const block of screen.blocks ?? []) {
    if (hidden.has(block.id)) continue;
    collectImageUrls(block, 1, urls);
  }
  return dedupe(urls);
}

function dedupe(urls: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    if (seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

/** Цели вариантов ВИДИМЫХ блоков текущего экрана, в порядке блоков и опций. */
function branchTargets(
  currentScreen: EditorScreen,
  logic: Logic,
  context: EvalContext,
): Array<{ screenId: string; optionId: string }> {
  const hidden = resolveVisibility(currentScreen.blocks ?? [], logic, context);
  const targets: Array<{ screenId: string; optionId: string }> = [];
  for (const block of currentScreen.blocks ?? []) {
    if (hidden.has(block.id)) continue;
    // WEB-3718: варианты — по имени кабинета (`imageChoiceOptions` у выбора
    // картинкой) через общий резолвер, и только у блоков, где плеер
    // исполняет переход по варианту (одиночный выбор, `blockHonorsOptionNav`):
    // раньше читалось одно `options`, и у выбора картинкой цель перехода не
    // подгружалась вовсе.
    if (!blockHonorsOptionNav(block)) continue;
    for (const option of resolveEditorBlockOptions(block as EditorBlock)) {
      const targetId = (option as { nextScreenId?: string }).nextScreenId;
      const optionId = (option as { id?: string }).id;
      if (!targetId || !optionId) continue;
      targets.push({ screenId: targetId, optionId });
    }
  }
  return targets;
}

export function nextScreenImageCandidates(
  input: NextScreenImageCandidatesInput,
): NextScreenImagePlan {
  const {
    screens,
    currentIndex,
    logic,
    context,
    paywallFirstScreen,
    sizeOfImage,
    maxImages = PREFETCH_MAX_IMAGES,
    maxBytes = PREFETCH_MAX_BYTES,
    maxPerCandidate = Number.POSITIVE_INFINITY,
  } = input;

  const currentScreen = screens[currentIndex];
  if (!currentScreen) return { candidates: [], urls: [] };

  const byId = new Map<string, EditorScreen>();
  for (const screen of screens) byId.set(screen.id, screen);

  const candidates: ScreenImageCandidate[] = [];
  const indexByScreenId = new Map<string, number>();

  const push = (
    screen: EditorScreen,
    isDefault: boolean,
    optionId?: string,
  ): void => {
    const existing = indexByScreenId.get(screen.id);
    if (existing !== undefined) {
      if (optionId) candidates[existing].optionIds.push(optionId);
      return;
    }
    indexByScreenId.set(screen.id, candidates.length);
    candidates.push({
      screenId: screen.id,
      optionIds: optionId ? [optionId] : [],
      urls: screenImageUrls(screen, logic, context),
      isDefault,
    });
  };

  // Ступень по умолчанию идёт ПЕРВОЙ: по ней уходит большинство покупателей.
  const nextByOrder = screens[currentIndex + 1];
  if (nextByOrder) {
    push(nextByOrder, true);
  } else if (paywallFirstScreen) {
    // Последний экран квиза ведёт на пейволл (WEB-744 §3).
    push(paywallFirstScreen, true);
  }

  for (const target of branchTargets(currentScreen, logic, context)) {
    const screen = byId.get(target.screenId);
    if (!screen || screen.id === currentScreen.id) continue;
    push(screen, false, target.optionId);
  }

  // Плоский список: лимит на кандидата, потом общие лимиты по числу и объёму.
  const urls: string[] = [];
  const seen = new Set<string>();
  let bytes = 0;
  outer: for (const candidate of candidates) {
    let takenHere = 0;
    for (const url of candidate.urls) {
      if (takenHere >= maxPerCandidate) break;
      if (seen.has(url)) continue;
      if (urls.length >= maxImages) break outer;
      const size = sizeOfImage?.(url);
      if (size !== undefined && bytes + size > maxBytes) break outer;
      if (size !== undefined) bytes += size;
      seen.add(url);
      urls.push(url);
      takenHere += 1;
    }
  }

  return { candidates, urls };
}
